<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Attendance Recap
                </h1>
                <p
                    class="text-sm text-gray-500 dark:text-gray-400 mt-1"
                    v-if="recap"
                >
                    {{ recap.class_subject.class?.name }} -
                    {{ recap.class_subject.subject?.name }} ·
                    {{ recap.total_sessions }} session(s)
                </p>
            </div>
            <button
                @click="goBack"
                class="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
                Back
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <div class="flex flex-wrap gap-3">
                    <!-- Class Subject Filter -->
                    <SearchableSelect
                        v-model="classSubjectId"
                        :options="classSubjectOptions"
                        placeholder="Select Class Subject"
                        :option-label="getClassSubjectLabel"
                        option-value="id"
                        class="flex-1 min-w-[220px]"
                        @update:model-value="fetchRecap"
                    />

                    <!-- Date Range -->
                    <DateRangePicker
                        v-model="dateRange"
                        class="flex-1 min-w-[200px]"
                        @update:model-value="fetchRecap"
                    />

                    <button
                        @click="resetFilters"
                        v-if="
                            classSubjectId ||
                            dateRange.date_from ||
                            dateRange.date_to
                        "
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="7" />
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ error }}
                    </p>
                    <button
                        @click="clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Recap Table -->
        <div
            v-if="!loading && !error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div v-if="!classSubjectId" class="p-4">
                <EmptyState
                    icon="BarChart3"
                    title="Select a class subject"
                    description="Pick a class subject to see the attendance recap of its students."
                />
            </div>

            <template v-else>
                <div class="overflow-x-auto">
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Student
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    NISN
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Present
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Absent
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Late
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Excused
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Percentage
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="row in recap?.students || []"
                                :key="row.student.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ row.student.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ row.student.nisn || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-success-600 dark:text-success-400"
                                >
                                    {{ row.present_count }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-danger-600 dark:text-danger-400"
                                >
                                    {{ row.absent_count }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-warning-600 dark:text-warning-400"
                                >
                                    {{ row.late_count }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-primary-600 dark:text-primary-400"
                                >
                                    {{ row.excused_count }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ row.attendance_percentage }}%
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <EmptyState
                    v-if="(recap?.students?.length || 0) === 0"
                    icon="UsersIcon"
                    title="No enrolled students"
                    description="No students are enrolled in this class for the active semester."
                />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import SearchableSelect from "@/components/SearchableSelect.vue";
import DateRangePicker, {
    type DateRangeValue,
} from "@/components/DateRangePicker.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import { RotateCcw } from "@lucide/vue";
import attendanceApi, { AttendanceRecap } from "@/api/attendance";
import { classSubjectApi } from "@/api/classSubject";

const route = useRoute();
const router = useRouter();

const classSubjectId = ref<string>("");
const dateRange = ref<DateRangeValue>({ date_from: null, date_to: null });
const recap = ref<AttendanceRecap | null>(null);
const classSubjectOptions = ref<any[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

async function fetchRecap() {
    if (!classSubjectId.value) {
        recap.value = null;
        return;
    }

    loading.value = true;
    error.value = null;

    try {
        recap.value = await attendanceApi.getRecap(classSubjectId.value, {
            date_from: dateRange.value.date_from,
            date_to: dateRange.value.date_to,
        });
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch attendance recap";
    } finally {
        loading.value = false;
    }
}

function resetFilters() {
    classSubjectId.value = "";
    dateRange.value = { date_from: null, date_to: null };
    recap.value = null;
}

async function fetchClassSubjects() {
    try {
        const response = await classSubjectApi.getAll({ per_page: 1000 });
        classSubjectOptions.value = response.data.items || [];
    } catch {
        classSubjectOptions.value = [];
    }
}

function getClassSubjectLabel(option: any) {
    return `${option.class?.grade_level || ""} ${option.class?.name || ""} - ${option.subject?.name || ""} (${option.semester?.name || "-"})`;
}

function goBack() {
    router.push("/attendance");
}

function clearError() {
    error.value = null;
}

onMounted(() => {
    fetchClassSubjects();

    const preselected = route.query.class_subject_id;
    if (typeof preselected === "string" && preselected) {
        classSubjectId.value = preselected;
        fetchRecap();
    }
});
</script>
